import reduce from 'lodash/reduce';

import * as D from './deps';

/**
 * Handle load stations from server
 *
 * @param  {number} pageSize - number of stations to return - defaulted to maximum
 * @returns {Promise}
 */
export function getStations({
  listenerId,
  sortBy,
  sortAscending,
  pageSize = 250,
  startIndex,
  startsWith,
}) {
  D.Utils.Time.markTimeAtMoment(D.Constants.App.ApiCheckpointMarks.FETCH_STATIONS_REQUEST);
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.FETCH_STATIONS,
    content: {
      listenerId,
      pageSize,
      sortAscending,
      sortBy,
      startIndex,
      startsWith,
    },
  });
}

export function getOrSetShuffleStations(stationIds, shuffleType) {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.GET_OR_SET_SHUFFLE_STATIONS,
    content: {
      stationIds,
      shuffleType,
    },
  });
}

/**
 * Handle remove station
 *
 * @param  {string} stationId - the station to remove
 * @returns {Promise}
 */
export function removeStation(stationId) {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.REMOVE_STATION,
    content: {
      stationId,
    },
  });
}

/**
 * Handle remove station using GraphQL endpoint,
 *
 * @param  {string} pandoraId - the station to remove
 * @returns {Promise}
 */
export function removeStationGraphQL(pandoraId) {
  // TODO:WEBCA-2365 Clean up legacy endpoints, rename once the legacy is removed
  const requestParams = D.GraphQL.Mutations.removeFromLibrary({
    pandoraId,
  });
  return D.Services.GraphQL.graphQLRequest(requestParams)();
}

/**
 * Handle create station
 *
 * @param  {string} stationCode - specially formatted string that starts with two letters to indicate type (see StationKeyTypeEnum.java) followed by an identifier
 * @param  {string} stationName - name of the station (optional)
 * @param  {string} searchQuery - query used to find a result (either autocomplete or extended search)that led to the station creation (for analytics)
 * @param  {string} pandoraId - required to create a station when there is no stationCode present
 * @param  {string} creativeId - creative ID of display ad used to create the station (optional)
 * @param  {string} lineId - lineId ID of display ad used to create the station (optional)
 * @param  {string} creationSource - creation source of display ad used to create the station (optional)
 * @param  {string} modeId - auto sets the mode id on the backend before discovery tuner (optional)
 * @returns {Promise}
 */
export function createStation(
  stationCode,
  stationName,
  searchQuery,
  pandoraId,
  creativeId,
  lineId,
  creationSource,
  modeId,
) {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.CREATE_STATION,
    content: {
      creationSource,
      creativeId,
      lineId,
      modeId,
      pandoraId,
      searchQuery,
      stationCode,
      stationName,
    },
  });
}

/**
 * Action creator for adding thumb related feedback
 *
 * @param  {object} options - list of options passed into content
 * @param  {string} options.trackToken - token for the track
 * @param  {boolean} options.isPositive - true for thumbs up, false for thumb down
 * @param  {string} options.feedbackId - music id for a track being added to a station feedback list
 * @param  {string} options.feedbackId - feedback id being added to a station feedback list
 * @param  {string} options.stationId - the station we are adding the thumb to
 * @param  {Array} options.relatedTrackTokens - all tokens related to the music id we're adding feedback to, including options.trackToken
 * @param options.wasTrackThumbedUp
 * @param options.musicId
 * @returns {Promise}
 */
export function addFeedback({
  trackToken,
  isPositive,
  wasTrackThumbedUp,
  musicId,
  relatedTrackTokens,
}) {
  const updateEntitiesWith = reduce(
    relatedTrackTokens,
    (result, token) => {
      result[token] = { rating: isPositive ? 1 : -1 };
      return result;
    },
    {},
  );
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.ADD_FEEDBACK,
    content: {
      trackToken,
      isPositive,
    },
    actionParams: {
      updateEntitiesWith,
      isPositive,
      wasTrackThumbedUp,
      musicId,
    },
  });
}

/**
 * Action creator for deleting thumb related feedback
 *
 * @param  {object} options - list of options passed into content
 * @param  {string} options.trackToken - token for the track
 * @param  {boolean} options.isPositive - true for thumbs up, false for thumb down
 * @param  {string} options.musicId - music id for a track being removed from a station feedback list
 * @param  {string} options.feedbackId - feedback id being removed from a station feedback list
 * @param  {string} options.stationId - the station we are removing the thumb from
 * @param  {Array} options.relatedTrackTokens - all tokens related to the music id we're removing feedback from, including options.trackToken
 * @returns {Promise}
 */
export function deleteFeedback({
  trackToken,
  isPositive,
  musicId,
  feedbackId,
  stationId,
  relatedTrackTokens,
}) {
  const updateEntitiesWith = reduce(
    relatedTrackTokens,
    (result, token) => {
      result[token] = { rating: 0 };
      return result;
    },
    {},
  );
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.DELETE_FEEDBACK,
    content: {
      trackToken,
      feedbackId,
      isPositive,
    },
    actionParams: {
      updateEntitiesWith,
      isPositive,
      musicId,
      stationId,
    },
  });
}

export function verifyHybridStationChecksum({ stationId, checksum }) {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.VERIFY_HYBRID_STATION_CHECKSUM,
    content: {
      stationId,
      checksum,
    },
  });
}

export function getSeedSuggestions({ stationId, seedMusicId, maxResults }) {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.GET_SEED_SUGGESTIONS,
    content: {
      stationId,
      seedMusicId,
      maxResults,
    },
  });
}

export function getStationDetails({ stationId, isCurrentStation }) {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.GET_STATION_DETAILS,
    content: {
      stationId,
      isCurrentStation,
    },
  });
}

export function getSeeds({ stationId }) {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.GET_SEEDS,
    content: {
      stationId,
    },
  });
}

export function addSeed({ stationId, musicId }) {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.ADD_SEED,
    content: {
      stationId,
      musicId,
    },
  });
}

export function addSeedGraphQL({ stationId, seedId }) {
  const requestParams = D.GraphQL.Mutations.addSeedToStation({
    stationId,
    seedId,
  });
  return D.Services.GraphQL.graphQLRequest(requestParams)();
}

export function deleteSeed({ stationId, musicId }) {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.DELETE_SEED,
    content: {
      stationId,
      musicId,
    },
  });
}

export function deleteSeedGraphQL({ stationId, seedId }) {
  const requestParams = D.GraphQL.Mutations.deleteSeedFromStation({
    stationId,
    seedId,
  });
  return D.Services.GraphQL.graphQLRequest(requestParams)();
}

export function getStationFeedback(data) {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.GET_STATION_FEEDBACK,
    content: {
      pageSize: data.pageSize,
      startIndex: data.startIndex,
      stationId: data.stationId,
      positive: data.positive,
    },
    actionParams: {
      stationId: data.stationId,
      mostRecent: data.mostRecent,
    },
  });
}

export function getStationRecommendations() {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.SEARCH_RECOMMENDATIONS,
    retries: 3,
  });
}

/**
 * Api call for recording various metrics around delivery of artist audio messages
 *
 * @param  {object} options - list of options passed into content
 * @param  {string} options.artistMessageId - ID for artist message
 * @param  {string} options.artistUid - Uid for artist
 * @param  {string} options.metric - metric to register (e.g. AUDIO_IMPRESSION, AUDIO_TILE_CLICK)
 * @param  {string} options.isOnDemand - true if being played on demand (from a deep link)
 * @param  {string} options.referrer - referrer url for current page
 * @returns {object} - an object with the ApiToken key, which
 * indicates that the action will pass through the API middleware.
 */
export function insertArtistMessageMetric({
  artistMessageId,
  artistUid,
  metric,
  isOnDemand,
  referrer,
}) {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.INSERT_ARTIST_MESSAGE_METRIC,
    content: {
      artistMessageId,
      artistUid,
      isOnDemand,
      metric,
      referrer,
    },
  });
}

/**
 * Api call for recording various metrics around delivery of voice tracks
 *
 * @param  {object} options - list of options passed into content
 * @param  {string} options.stationId - ID of the station
 * @param  {string} options.voiceTrackPandoraId - pandoraId of the voice track
 * @param  {string} options.authorPandoraId - pandoraId of the voice track author
 * @param  {string} options.statType - metric to register (e.g. AUDIO_IMPRESSION, AUDIO_TILE_CLICK)
 * @returns {object} - an object with the ApiToken key, which
 * indicates that the action will pass through the API middleware.
 */
export function insertVoiceTrackMetric({
  stationId,
  voiceTrackPandoraId,
  authorPandoraId,
  statType,
}) {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.INSERT_VOICE_TRACK_METRIC,
    content: {
      stationId,
      voiceTrackPandoraId,
      authorPandoraId,
      statType,
    },
  });
}

export function getFragment({
  stationId,
  isStationStart = false,
  fragmentRequestReason,
  audioFormat,
  deltaListeningSeconds,
  listeningTimestamp,
  lastPlayedTrackToken,
  startingAtTrackId,
  testArtistMessage,
  testVoiceTrack,
  onDemandArtistMessageArtistUidHex,
  onDemandArtistMessageIdHex,
  isPrefetch,
} = {}) {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.GET_FRAGMENT,
    content: {
      audioFormat,
      deltaListeningSeconds,
      fragmentRequestReason,
      isStationStart,
      lastPlayedTrackToken,
      listeningTimestamp,
      onDemandArtistMessageArtistUidHex,
      onDemandArtistMessageIdHex,
      startingAtTrackId,
      stationId,
      testArtistMessage,
      testVoiceTrack,
    },
    actionParams: {
      updateEntitiesWith: {
        audioFormat,
        stationId,
      },
      isPrefetch,
    },
  });
}

export function addTiredSong(trackToken) {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.ADD_TIRED_SONG,
    content: {
      trackToken: {
        value: trackToken,
      },
    },
  });
}

export function actionSnooze({ sourceId, trackToken, index, elapsedTime, deviceUuid }) {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.ACTION_SNOOZE,
    content: {
      deviceUuid,
      elapsedTime,
      index,
      sourceId,
      trackToken,
    },
  });
}

export function setInteractiveRadioMode({ stationId, modeId, previousModeId }) {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.INTERACTIVE_RADIO_SET_MODE,
    content: {
      stationId,
      modeId,
      previousModeId,
    },
  });
}

export function getInteractiveRadioModes({ stationId }) {
  return D.Api.apiRequest({
    endpoint: D.Constants.Api.ApiEndpoints.INTERACTIVE_RADIO_GET_MODES,
    content: {
      stationId,
    },
    retries: 3,
  });
}
